// src/pages/ConversationItemTestPage.tsx
import React, { useState, useEffect } from 'react';
import { ConversationItem } from '@components/ConversationItem';
import ConversationApi from '@components/ConversationItem/api/ConversationApi';
import type { ConversationDetail } from '../types/conversation';
import type { ApiConfig, CorgeeFunctions, AppConfig } from '@components/ConversationItem/types';
import { authStorage } from '../lib/storage';

// Helper function to get dynamic API configuration
const getApiConfig = (): ApiConfig =>
{
  const token = authStorage.getAccessToken() || '';
  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost/Corgee.Web/';
  return {
    url: apiUrl,
    token: token,
  };
};

// Helper function to get dynamic App configuration
const getAppConfig = (): AppConfig =>
{
  const webUrl = import.meta.env.VITE_WEB_URL || 'http://localhost/Corgee.WebClient/';
  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost/Corgee.Web/';
  return {
    weburl: webUrl,
    apiurl: apiUrl,
  };
};

// Real Item Types Configuration
const realItemTypes = {
  Users: {
    Name: 'People',
    Icon: 'icons-menu-account-box',
  },
  Tasks: {
    Name: 'Tasks',
    Icon: 'icons-menu-epic',
  },
};

// Menu Configuration
const configMenus = {
  conversation: [
    'goto-project',
    'close-infowindow',
    'opendetails-newtab',
    'mark-redflag',
    'mark-important',
    'follow-conv',
    'copylink-to-clipboard',
    'create-itemtype',
    'delete',
    'duplicate-item',
    'archive',
    'submit-time',
    'edit-history',
  ],
  reply: ['mark-favorite', 'repost', 'delete', 'edit', 'create-itemtype', 'mark-important'],
};

// Storage Data Configuration
const storageData = {
  data: {
    isAdmin: true,
    isAdminProject: false,
    canEdit: true,
    canDelete: true,
    ImageProfile: 'acbbf4b2-4a1b-40b4-a8b9-c05df97e491e.jpg',
    External: false,
    ExternalLogin: false,
    SupportPermissions: false,
    EmailAddress: 'pacoTest01@hotmail.com',
  },
  allLayers: [
    {
      LayerId: 1,
      Name: 'Personal',
      Color: '#ffc989',
      TextColor: true,
      IsEnable: false,
      IsDefault: false,
      PriorityInConfigValue: 0,
    },
    {
      LayerId: 2,
      Name: 'Production',
      Color: '#7cc5dc',
      TextColor: true,
      IsEnable: false,
      IsDefault: false,
      PriorityInConfigValue: 0,
    },
    {
      LayerId: 4,
      Name: 'Client',
      Color: '#05fa00',
      TextColor: true,
      IsEnable: false,
      IsDefault: false,
      PriorityInConfigValue: 0,
    },
    {
      LayerId: 5,
      Name: 'HR',
      Color: '#882e2e',
      TextColor: false,
      IsEnable: false,
      IsDefault: false,
      PriorityInConfigValue: 0,
    },
    {
      LayerId: 8,
      Name: 'Social',
      Color: '#c094d6',
      TextColor: false,
      IsEnable: false,
      IsDefault: false,
      PriorityInConfigValue: 0,
    },
    {
      LayerId: 10,
      Name: 'Developers',
      Color: '#4a4343',
      TextColor: false,
      IsEnable: false,
      IsDefault: false,
      PriorityInConfigValue: 0,
    },
    {
      LayerId: 12,
      Name: 'Support',
      Color: '#7cc899',
      TextColor: false,
      IsEnable: false,
      IsDefault: false,
      PriorityInConfigValue: 0,
    },
    {
      LayerId: 20,
      Name: 'QA',
      Color: '#125ee2',
      TextColor: false,
      IsEnable: false,
      IsDefault: false,
      PriorityInConfigValue: 0,
    },
    {
      LayerId: 24,
      Name: '3qa',
      Color: '#ea3b35',
      TextColor: false,
      IsEnable: false,
      IsDefault: false,
      PriorityInConfigValue: 0,
    },
    {
      LayerId: 277,
      Name: 'New Layer4',
      Color: '#f4ff30',
      TextColor: true,
      IsEnable: false,
      IsDefault: false,
      PriorityInConfigValue: 0,
    },
  ],
  layersToHide: [],
  layerId: 1,
  conv_maxAllowedUpload: 20480000,
  enableChat: true,
  personid: 7332,
};

const ConversationItemTestPage: React.FC = () =>
{
  // Dynamic API and App configuration from session
  const [api, setApi] = useState<ApiConfig>(() => getApiConfig());
  const [config] = useState<AppConfig>(() => getAppConfig());

  const [conversation, setConversation] = useState<ConversationDetail | null>(null);
  const [conversationId, setConversationId] = useState<number>(185343);
  const [inputId, setInputId] = useState<string>('185343');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isViewCard, setIsViewCard] = useState<boolean>(true);
  const [isConversationDetailsView, setIsConversationDetailsView] = useState<boolean>(true);

  // Refresh API config when component mounts (in case token changed)
  useEffect(() =>
  {
    setApi(getApiConfig());
  }, []);

  // Corgee Functions (event handlers)
  const corgeeFunctions: CorgeeFunctions = {
    onSendError: (_convId: number) => {},
    loadingBarStart: () => {},
    loadingBarComplete: () => {},
    callBackUpdateComplete: (_data: any) => {},
    sendToBookmarks: (_conv: ConversationDetail) => {},
    moveTo: (_event: React.MouseEvent, _conv: ConversationDetail) => {},
    copyTo: (_event: React.MouseEvent, _conv: ConversationDetail) => {},
    onChangedProject: (_convId: number, _projectId: number) => {},
    onChangedSecurityGroups: (_convId: number, _groups: any[]) => {},
    onChangedGroupLayers: (_convId: number, _layers: any[]) => {},
    onChangeOwners: (_convId: number, _owners: any[]) => {},
  };

  // Event handlers for conversation updates
  const onUpdateConversation = (
    _convId: number,
    updates: Partial<ConversationDetail>,
    skipUpdate?: boolean,
    _source?: string,
  ) =>
  {
    if (conversation && !skipUpdate)
    {
      setConversation(prev => (prev ? { ...prev, ...updates } : null));
    }
  };

  const onAddReply = (_convId: number, reply: any) =>
  {
    if (conversation)
    {
      setConversation(prev =>
      {
        if (!prev) return null;
        const replies = prev.Replies || [];
        return {
          ...prev,
          TotalReplies: (prev.TotalReplies || 0) + 1,
          Replies: [...replies, reply],
        };
      });
    }
  };

  const onUpdateReply = (_convId: number, _replyId: number, _updates: any) => {};

  const onDeleteReply = (_convId: number, _replyId: number) => {};

  const onChangedLayers = (_params: any, _isFromInfoWindow?: boolean) => {};

  const onAddLayerToCard = (_e: React.MouseEvent | MouseEvent, _layer: any) => {};

  // Fetch conversation by ID
  const fetchConversation = async (id: number) =>
  {
    setLoading(true);
    setError(null);

    try
    {
      const requestParams = {
        conversationId: id,
        layersToHide: '',
        getAllReplies: false,
        isConversationDetailsView: true,
        totalReplies: 0,
      };

      const response = await ConversationApi.getConversationById(api, requestParams);

      if (response)
      {
        // Fix image paths for replies
        if (response.Replies)
        {
          for (let i = 0; i < response.Replies.length; i++)
          {
            if (response.Replies[i].ImageName === 'Images/user.png')
            {
              response.Replies[i].ImageName = '../../Images/user.png';
            }
          }
        }
        setConversation(response);
      }
      else
      {
        setError('No conversation found with ID: ' + id);
      }
    }
    catch (err)
    {
      console.error('Error fetching conversation:', err);
      setError('Failed to fetch conversation. Check console for details.');
    }
    finally
    {
      setLoading(false);
    }
  };

  // Load conversation on mount or when conversationId changes
  useEffect(() =>
  {
    fetchConversation(conversationId);
  }, [conversationId]);

  // Handle form submission to load new conversation
  const handleLoadConversation = (e: React.FormEvent) =>
  {
    e.preventDefault();
    const newId = parseInt(inputId, 10);
    if (!isNaN(newId) && newId > 0)
    {
      setConversationId(newId);
    }
  };

  return (
    <div
      style={{
        padding: '20px',
        maxWidth: '1200px',
        margin: '0 auto',
        backgroundColor: '#f5f5f5',
        minHeight: '100vh',
      }}
    >
      <h1 style={{ marginBottom: '20px', color: '#333' }}>ConversationItem Test Page</h1>

      {/* Configuration Controls */}
      <section
        style={{
          marginBottom: '20px',
          padding: '20px',
          border: '1px solid #ddd',
          borderRadius: '8px',
          backgroundColor: 'white',
        }}
      >
        <h2 style={{ marginBottom: '15px', borderBottom: '1px solid #eee', paddingBottom: '10px' }}>
          Configuration
        </h2>

        {/* Load Conversation Form */}
        <form onSubmit={handleLoadConversation} style={{ marginBottom: '15px' }}>
          <label style={{ marginRight: '10px', fontWeight: 'bold' }}>Conversation ID:</label>
          <input
            type="number"
            value={inputId}
            onChange={e => setInputId(e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              marginRight: '10px',
              width: '120px',
            }}
          />
          <button
            type="submit"
            style={{
              padding: '8px 16px',
              backgroundColor: '#1877f2',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            Load Conversation
          </button>
        </form>

        {/* View Options */}
        <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input
              type="checkbox"
              checked={isViewCard}
              onChange={e => setIsViewCard(e.target.checked)}
            />
            <span>isViewCard</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input
              type="checkbox"
              checked={isConversationDetailsView}
              onChange={e => setIsConversationDetailsView(e.target.checked)}
            />
            <span>isConversationDetailsView</span>
          </label>
        </div>
      </section>

      {/* Status Messages */}
      {loading && (
        <div
          style={{
            padding: '15px',
            backgroundColor: '#e7f3ff',
            borderRadius: '4px',
            marginBottom: '20px',
            textAlign: 'center',
          }}
        >
          Loading conversation...
        </div>
      )}

      {error && (
        <div
          style={{
            padding: '15px',
            backgroundColor: '#ffebee',
            borderRadius: '4px',
            marginBottom: '20px',
            color: '#c62828',
          }}
        >
          {error}
        </div>
      )}

      {/* ConversationItem Component */}
      {conversation && (
        <section
          style={{
            marginBottom: '20px',
            padding: '20px',
            border: '1px solid #ddd',
            borderRadius: '8px',
            backgroundColor: 'white',
          }}
        >
          <h2
            style={{ marginBottom: '15px', borderBottom: '1px solid #eee', paddingBottom: '10px' }}
          >
            Conversation #{conversation.ConversationId}
          </h2>

          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            <ConversationItem
              index={1}
              api={api}
              corgeeFunctions={corgeeFunctions}
              conversation={conversation}
              isViewCard={isViewCard}
              isConversationDetailsView={isConversationDetailsView}
              onUpdateConversation={onUpdateConversation}
              onAddReply={onAddReply}
              onUpdateReply={onUpdateReply}
              onDeleteReply={onDeleteReply}
              onChangedLayers={onChangedLayers}
              onAddLayerToCard={onAddLayerToCard}
              hasArchivedFilter={false}
              realItemTypes={realItemTypes}
              ConfigMenus={configMenus}
              config={config}
              storageData={storageData}
            />
          </ul>
        </section>
      )}

      {/* Debug Info */}
      <details style={{ marginTop: '20px' }}>
        <summary style={{ cursor: 'pointer', fontWeight: 'bold', marginBottom: '10px' }}>
          Debug: Conversation Data
        </summary>
        <pre
          style={{
            backgroundColor: '#f8f9fa',
            padding: '15px',
            borderRadius: '4px',
            overflow: 'auto',
            maxHeight: '400px',
            fontSize: '12px',
          }}
        >
          {JSON.stringify(conversation, null, 2)}
        </pre>
      </details>
    </div>
  );
};

export default ConversationItemTestPage;
